import { Schema } from 'effect';
import { HttpApiEndpoint, HttpApiGroup, HttpApiSchema } from 'effect/unstable/httpapi';

/**
 * [A.6.5a] `/v1/jobs` contract — single source of truth for the jobs slice.
 *
 * opencode-style split: this effect `HttpApi` group is the one definition both
 * sides build against. The server (`apps/api/server/src/job-index.ts` response
 * builders) typechecks its payloads against these schemas and a vitest decodes
 * real builder output with them; the web dashboard consumes
 * `makeJobsClient` from `./jobs-client.js` instead of hand-declaring URLs and
 * response interfaces. Schemas are wire-honest: fields the server omits for
 * some job types (e.g. `document_id` on public parse jobs) are optional here,
 * and the client facade normalizes into the dashboard's stricter view.
 */

export const JobStatus = Schema.Literals([
  'queued',
  'running',
  'idle',
  'awaiting_user_input',
  'succeeded',
  'completed',
  'completed_with_errors',
  'failed',
  'cancelled',
]);
export type JobStatus = Schema.Schema.Type<typeof JobStatus>;

export const JobProgress = Schema.Struct({
  phase: Schema.optionalKey(Schema.String),
  current: Schema.Number,
  total: Schema.Number,
  pages_completed: Schema.Number,
  pages_failed: Schema.Number,
  pages_running: Schema.Number,
  pages_pending: Schema.Number,
  pages_total: Schema.Number,
  chunks_completed: Schema.Number,
  chunks_total: Schema.Number,
});
export type JobProgress = Schema.Schema.Type<typeof JobProgress>;

export const Job = Schema.Struct({
  object: Schema.Literal('job'),
  id: Schema.String,
  type: Schema.optionalKey(Schema.String),
  job_id: Schema.String,
  job_type: Schema.String,
  job_label: Schema.optionalKey(Schema.NullOr(Schema.String)),
  status: JobStatus,
  document_id: Schema.optionalKey(Schema.NullOr(Schema.String)),
  file_name: Schema.optionalKey(Schema.NullOr(Schema.String)),
  run_id: Schema.optionalKey(Schema.NullOr(Schema.String)),
  workflow_name: Schema.optionalKey(Schema.NullOr(Schema.String)),
  workflow_id: Schema.optionalKey(Schema.NullOr(Schema.String)),
  engine_id: Schema.optionalKey(Schema.NullOr(Schema.String)),
  provider: Schema.optionalKey(Schema.NullOr(Schema.String)),
  model: Schema.optionalKey(Schema.NullOr(Schema.String)),
  progress_current: Schema.Number,
  progress_total: Schema.Number,
  progress: Schema.optionalKey(JobProgress),
  pages_completed: Schema.Number,
  pages_failed: Schema.Number,
  pages_running: Schema.Number,
  pages_pending: Schema.Number,
  pages_total: Schema.Number,
  chunks_completed: Schema.Number,
  chunks_total: Schema.Number,
  cost_usd: Schema.optionalKey(Schema.Number),
  duration_ms: Schema.optionalKey(Schema.NullOr(Schema.Number)),
  error: Schema.optionalKey(Schema.NullOr(Schema.String)),
  latest_error: Schema.optionalKey(Schema.NullOr(Schema.String)),
  request: Schema.optionalKey(Schema.Unknown),
  result: Schema.optionalKey(Schema.Unknown),
  metadata: Schema.optionalKey(Schema.Record(Schema.String, Schema.Unknown)),
  created_at: Schema.Number,
  updated_at: Schema.Number,
  completed_at: Schema.optionalKey(Schema.NullOr(Schema.Number)),
});
export type Job = Schema.Schema.Type<typeof Job>;

export const JobList = Schema.Struct({
  object: Schema.Literal('list'),
  data: Schema.Array(Job),
  has_more: Schema.Boolean,
  next_cursor: Schema.NullOr(Schema.String),
});
export type JobList = Schema.Schema.Type<typeof JobList>;

export const JobPageStatus = Schema.Literals([
  'pending',
  'queued',
  'running',
  'completed',
  'error',
  'cancelled',
]);
export type JobPageStatus = Schema.Schema.Type<typeof JobPageStatus>;

export const JobPageRow = Schema.Struct({
  run_id: Schema.String,
  page_number: Schema.Number,
  status: JobPageStatus,
  chunk_index: Schema.Number,
  markdown: Schema.optionalKey(Schema.NullOr(Schema.String)),
  nodes_count: Schema.optionalKey(Schema.NullOr(Schema.Number)),
  anchors_count: Schema.optionalKey(Schema.NullOr(Schema.Number)),
  error: Schema.optionalKey(Schema.NullOr(Schema.String)),
  started_at: Schema.optionalKey(Schema.NullOr(Schema.Number)),
  completed_at: Schema.optionalKey(Schema.NullOr(Schema.Number)),
  updated_at: Schema.optionalKey(Schema.NullOr(Schema.Number)),
});
export type JobPageRow = Schema.Schema.Type<typeof JobPageRow>;

export const JobChunkStatus = Schema.Literals([
  'pending',
  'running',
  'completed',
  'failed',
  'cancelled',
  'completed_with_errors',
]);
export type JobChunkStatus = Schema.Schema.Type<typeof JobChunkStatus>;

export const JobChunkRow = Schema.Struct({
  run_id: Schema.String,
  chunk_index: Schema.Number,
  start_page: Schema.Number,
  end_page: Schema.Number,
  pages_total: Schema.Number,
  pages_completed: Schema.Number,
  pages_failed: Schema.Number,
  pages_running: Schema.Number,
  pages_pending: Schema.Number,
  pages_cancelled: Schema.Number,
  status: JobChunkStatus,
  error: Schema.optionalKey(Schema.NullOr(Schema.String)),
  started_at: Schema.optionalKey(Schema.NullOr(Schema.Number)),
  completed_at: Schema.optionalKey(Schema.NullOr(Schema.Number)),
  updated_at: Schema.optionalKey(Schema.NullOr(Schema.Number)),
});
export type JobChunkRow = Schema.Schema.Type<typeof JobChunkRow>;

const jobChildList = <A, I, R>(data: Schema.Codec<A, I, R>) =>
  Schema.Struct({
    object: Schema.Literal('list'),
    job_id: Schema.String,
    run_id: Schema.NullOr(Schema.String),
    data: Schema.Array(data),
    has_more: Schema.Boolean,
    next_cursor: Schema.NullOr(Schema.String),
  });

export const JobPageList = jobChildList(JobPageRow);
export type JobPageList = Schema.Schema.Type<typeof JobPageList>;

export const JobChunkList = jobChildList(JobChunkRow);
export type JobChunkList = Schema.Schema.Type<typeof JobChunkList>;

/** Canonical error envelope — matches the server's `jobNotFoundJson` shape. */
export const JobError = Schema.Struct({
  error: Schema.Struct({
    code: Schema.String,
    message: Schema.String,
  }),
  code: Schema.optionalKey(Schema.String),
  message: Schema.optionalKey(Schema.String),
  next_actions: Schema.optionalKey(
    Schema.Array(Schema.Struct({ cmd: Schema.String, why: Schema.String })),
  ),
});
export type JobError = Schema.Schema.Type<typeof JobError>;

const JobNotFound = JobError.pipe(HttpApiSchema.status(404));
const JobBadRequest = JobError.pipe(HttpApiSchema.status(400));

const JobIdParam = Schema.Struct({ jobId: Schema.String });

const ListJobsQuery = Schema.Struct({
  status: Schema.optionalKey(JobStatus),
  limit: Schema.optionalKey(Schema.NumberFromString),
  /** Opaque `next_cursor` from the previous page; omit for the first page. */
  cursor: Schema.optionalKey(Schema.String),
  /** `'0'` skips the live Durable Object hydration of in-flight rows. */
  hydrate: Schema.optionalKey(Schema.Literals(['0', '1'])),
});

const ListJobPagesQuery = Schema.Struct({
  status: Schema.optionalKey(Schema.Union([JobPageStatus, Schema.Literal('failed')])),
  limit: Schema.optionalKey(Schema.NumberFromString),
});

const ListJobChunksQuery = Schema.Struct({
  status: Schema.optionalKey(Schema.Union([JobChunkStatus, Schema.Literal('error')])),
  limit: Schema.optionalKey(Schema.NumberFromString),
});

export const JobsApiGroup = HttpApiGroup.make('jobs')
  .add(
    HttpApiEndpoint.get('listJobs', '/v1/jobs', {
      query: ListJobsQuery,
      success: JobList,
      error: [JobBadRequest],
    }),
  )
  .add(
    HttpApiEndpoint.get('getJob', '/v1/jobs/:jobId', {
      params: JobIdParam,
      success: Job,
      error: [JobNotFound],
    }),
  )
  .add(
    HttpApiEndpoint.get('listJobPages', '/v1/jobs/:jobId/pages', {
      params: JobIdParam,
      query: ListJobPagesQuery,
      success: JobPageList,
      error: [JobNotFound],
    }),
  )
  .add(
    HttpApiEndpoint.get('listJobChunks', '/v1/jobs/:jobId/chunks', {
      params: JobIdParam,
      query: ListJobChunksQuery,
      success: JobChunkList,
      error: [JobNotFound],
    }),
  )
  .add(
    HttpApiEndpoint.post('cancelJob', '/v1/jobs/:jobId/cancel', {
      params: JobIdParam,
      success: Job,
      error: [JobNotFound, JobBadRequest],
    }),
  )
  .add(
    HttpApiEndpoint.post('retryJob', '/v1/jobs/:jobId/retry', {
      params: JobIdParam,
      success: Job,
      error: [JobNotFound, JobBadRequest],
    }),
  )
  .add(
    HttpApiEndpoint.post('resumeJob', '/v1/jobs/:jobId/resume', {
      params: JobIdParam,
      success: Job,
      error: [JobNotFound, JobBadRequest],
    }),
  );

/**
 * Synchronous decoders for conformance tests and server-side validation.
 * They let consumers (e.g. `apps/api/server` tests) validate real payloads
 * against the contract without taking a direct dependency on `effect`.
 */
export const decodeJob = Schema.decodeSync(Job);
export const decodeJobList = Schema.decodeSync(JobList);
export const decodeJobPageList = Schema.decodeSync(JobPageList);
export const decodeJobChunkList = Schema.decodeSync(JobChunkList);
export const decodeJobError = Schema.decodeSync(JobError);
